(() => {
  'use strict';
  const API = 'https://teambossnet.ro/clienti/edengarden-bridge/calendar';
  const memory = new Map();
  const pending = new Map();
  const pad = value => String(value).padStart(2, '0');
  const iso = date => date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  const parse = value => new Date(value + 'T12:00:00');
  const addDays = (value, count) => { const date = parse(value); date.setDate(date.getDate() + count); return iso(date); };
  const distance = (a, b) => Math.round((Date.parse(b + 'T12:00:00Z') - Date.parse(a + 'T12:00:00Z')) / 86400000);
  const today = () => {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/Bucharest',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date()).map(part => [part.type,part.value]));
    return parts.year + '-' + parts.month + '-' + parts.day;
  };
  const priceFormat = new Intl.NumberFormat(window.EdenI18n.locale, {maximumFractionDigits:2});
  const spokenDate = new Intl.DateTimeFormat(window.EdenI18n.locale, {day:'numeric',month:'long',year:'numeric'});
  const summaryDate = new Intl.DateTimeFormat(window.EdenI18n.locale, {day:'numeric',month:'short',year:'numeric'});

  async function fetchCalendar(request) {
    const key = 'eden.calendar.v1.' + JSON.stringify(request);
    let cached = memory.get(key);
    if (!cached) { try { cached = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch {} }
    if (cached && Date.now() - Date.parse(cached.checkedAt) < 120000) return cached;
    if (pending.has(key)) return pending.get(key);
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      try {
        const response = await fetch(window.EdenI18n.api(API), {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},credentials:'omit',body:JSON.stringify(window.EdenI18n.payload(request)),signal:controller.signal});
        const body = await response.json();
        if (!response.ok || body.status !== 'ok' || !body.data?.days || body.data.currency !== 'RON') throw new Error('Calendar unavailable');
        memory.set(key, body.data);
        try { sessionStorage.setItem(key, JSON.stringify(body.data)); } catch {}
        return body.data;
      } finally { clearTimeout(timeout); pending.delete(key); }
    })();
    pending.set(key, promise);
    return promise;
  }

  class EdenStayCalendar {
    constructor({root, checkin, checkout, hotelId, roomId}) {
      this.root = root;
      this.checkin = checkin;
      this.checkout = checkout;
      this.hotelId = Number(hotelId);
      this.roomId = roomId ? Number(roomId) : null;
      this.days = {};
      this.pickers = [];
      this.loading = 0;
      this.error = false;
      this.loadedAt = 0;
      this.today = today();
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.pickers.push(this.createPicker(checkin, 'in'));
      this.pickers.push(this.createPicker(checkout, 'out'));
      const syncLater = () => queueMicrotask(() => this.sync());
      checkin.addEventListener('change', syncLater);
      checkout.addEventListener('change', syncLater);
      checkin.form?.addEventListener('reset', syncLater);
      window.addEventListener('pageshow', syncLater);
      this.mobile.addEventListener('change', () => this.pickers.forEach(picker => picker.set('showMonths', this.mobile.matches ? 1 : 2)));
      this.preload = this.load(this.today, 60);
    }

    createPicker(input, role) {
      input.parentElement.classList.add('eg-calendar-field');
      input.setAttribute('aria-haspopup', 'dialog');
      input.setAttribute('aria-expanded', 'false');
      const picker = window.flatpickr(input, {
        locale: window.EdenI18n.en ? window.flatpickr.l10ns.default : window.flatpickr.l10ns.ro,
        dateFormat: 'Y-m-d',
        ariaDateFormat: 'j F Y',
        defaultDate: input.value || undefined,
        disableMobile: true,
        allowInput: false,
        animate: false,
        showMonths: this.mobile.matches ? 1 : 2,
        monthSelectorType: 'static',
        minDate: this.today,
        maxDate: addDays(this.today, 730),
        position: fp => this.position(fp),
        disable: [date => !this.canSelect(iso(date), role)],
        onDayCreate: (_, __, fp, day) => this.decorateDay(day, role),
        onReady: (_, __, fp) => {
          const calendar = fp.calendarContainer;
          calendar.classList.add('eden-calendar');
          calendar.dataset.step = role;
          calendar.setAttribute('role', 'dialog');
          calendar.setAttribute('aria-label', role === 'in' ? window.EdenI18n.t("text_0f40151cebe5") : window.EdenI18n.t("text_20e072962e84"));
          const heading = document.createElement('div');
          heading.className = 'eden-calendar__heading';
          const progress = document.createElement('div');
          progress.className = 'eden-calendar__progress';
          progress.setAttribute('role', 'status');
          progress.setAttribute('aria-live', 'polite');
          progress.setAttribute('aria-atomic', 'true');
          const confirmation = document.createElement('span');
          confirmation.className = 'eden-calendar__confirmation';
          const title = document.createElement('strong');
          title.className = 'eden-calendar__title';
          progress.append(confirmation, title);
          const close = document.createElement('button');
          close.type = 'button'; close.className = 'eden-calendar__close'; close.textContent = '×';
          close.setAttribute('aria-label', window.EdenI18n.t("text_e2427f4a85de"));
          close.addEventListener('click', () => { input.focus({preventScroll:true}); fp.close(); });
          heading.append(progress, close);
          calendar.prepend(heading);
          fp.edenRole = role; fp.edenConfirmation = confirmation; fp.edenTitle = title;
          this.setProgress(fp);
          const footer = document.createElement('div');
          footer.className = 'eden-calendar__footer';
          const note = document.createElement('p');
          note.textContent = window.EdenI18n.t("text_a01b58fc6edb");
          const status = document.createElement('p');
          status.className = 'eden-calendar__status'; status.setAttribute('role','status');
          const retry = document.createElement('button');
          retry.type = 'button'; retry.className = 'eden-calendar__retry'; retry.textContent = window.EdenI18n.t("text_e018c48a7c2e"); retry.hidden = true;
          retry.addEventListener('click', () => this.loadVisible(fp, true));
          footer.append(note, status, retry);
          calendar.append(footer);
          fp.edenStatus = status; fp.edenRetry = retry;
          this.setStatus(fp);
        },
        onOpen: (_, __, fp) => {
          document.body.append(fp.calendarContainer);
          input.setAttribute('aria-expanded', 'true');
          input.parentElement.classList.add('eg-calendar-field--active');
          this.sync();
          this.preload.then(() => this.queueVisible(fp, Date.now() - this.loadedAt > 120000));
        },
        onClose: () => {
          input.setAttribute('aria-expanded', 'false');
          input.parentElement.classList.remove('eg-calendar-field--active');
        },
        onMonthChange: (_, __, fp) => this.queueVisible(fp),
        onYearChange: (_, __, fp) => this.queueVisible(fp),
        onChange: () => {
          setTimeout(() => {
            this.sync();
            if (role === 'in' && this.checkin.value && !this.checkout.disabled) {
              const next = this.pickers[1];
              next.jumpToDate(this.checkin.value);
              next.open();
              this.checkout.focus({preventScroll:true});
            }
          }, 0);
        },
      });
      return picker;
    }

    canSelect(date, role) {
      if (date < this.today) return false;
      const day = this.days[date];
      if (role === 'in') return !day || day.canCheckIn;
      const start = this.checkin.value;
      if (!start || date <= start || distance(start, date) > 30) return false;
      const arrival = this.days[start];
      if (arrival && (!arrival.canCheckIn || distance(start, date) < arrival.minStay)) return false;
      if (day && !day.canCheckOut) return false;
      // A sold-out night may still be selected as the departure date.
      for (let night = start; night < date; night = addDays(night, 1)) {
        if (this.days[night] && !this.days[night].available) return false;
      }
      return true;
    }

    position(picker) {
      if (this.mobile.matches) return;
      const calendar = picker.calendarContainer;
      const anchor = picker.input.getBoundingClientRect();
      const width = calendar.offsetWidth;
      const height = calendar.offsetHeight;
      const left = Math.max(16, Math.min(window.innerWidth - width - 16, anchor.left + anchor.width / 2 - width / 2));
      let top = anchor.bottom + 8;
      if (top + height > window.innerHeight - 12) top = anchor.top - height - 8;
      if (top < 12) top = Math.max(12, (window.innerHeight - height) / 2);
      Object.assign(calendar.style, {position:'fixed',left:left+'px',top:top+'px',right:'auto',bottom:'auto'});
    }

    decorateDay(element, role) {
      const date = iso(element.dateObj);
      element.dataset.date = date;
      const state = this.days[date];
      const number = document.createElement('span');
      number.className = 'eden-calendar__day-number'; number.textContent = element.dateObj.getDate();
      const price = document.createElement('span');
      price.className = 'eden-calendar__day-price';
      const past = date < this.today;
      const start = this.checkin.value;
      const end = this.checkout.value;
      price.textContent = past ? '' : state?.price != null ? priceFormat.format(state.price) : state ? '—' : '…';
      element.replaceChildren(number, price);
      let label = spokenDate.format(element.dateObj);
      if (!past && state) {
        label += state.available ? ', disponibil' : ', indisponibil pentru cazare';
        if (state.price != null) label += window.EdenI18n.t("text_7adcf6ca4675") + priceFormat.format(state.price) + window.EdenI18n.t("text_432d2ff06b8e");
        if (role === 'out' && this.canSelect(date, role)) label += ', check-out permis';
        if (state.minStay > 1) label += ', minimum ' + state.minStay + window.EdenI18n.t("text_15b97e974ebe");
      }
      if (date === start) label += ', check-in ales';
      if (date === end && end > start) label += ', check-out ales';
      element.setAttribute('aria-label', label);
      element.title = label;
      element.classList.toggle('eden-calendar__sold-out', !past && Boolean(state) && !state.available);
      element.classList.toggle('eden-calendar__arrival', date === start);
      element.classList.toggle('eden-calendar__departure', date === end && end > start);
      element.classList.toggle('eden-calendar__in-range', Boolean(start && end && date > start && date < end));
    }

    sync() {
      for (let index = 0; index < this.pickers.length; index++) {
        const fp = this.pickers[index];
        const input = index === 0 ? this.checkin : this.checkout;
        const value = input.value;
        if (value && (!fp.selectedDates[0] || iso(fp.selectedDates[0]) !== value)) fp.setDate(value, false);
        if (!value && fp.selectedDates.length) fp.clear(false);
        this.setProgress(fp);
        fp.redraw();
      }
    }

    setProgress(picker) {
      if (!picker.edenTitle) return;
      const departure = picker.edenRole === 'out';
      const start = this.checkin.value;
      const confirmation = departure && start
        ? '✓ Check-in ales: ' + summaryDate.format(parse(start))
        : departure ? window.EdenI18n.t("text_c20b5cebb74f") : window.EdenI18n.t("text_835ad53c5316");
      const title = departure ? window.EdenI18n.t("text_1670da3712b7") : window.EdenI18n.t("text_c8bfe70bbbb3");
      if (picker.edenConfirmation.textContent !== confirmation) picker.edenConfirmation.textContent = confirmation;
      if (picker.edenTitle.textContent !== title) picker.edenTitle.textContent = title;
      picker.calendarContainer.setAttribute('aria-label', departure && start
        ? 'Check-in ales: ' + spokenDate.format(parse(start)) + window.EdenI18n.t("text_a28ef4e1f142")
        : departure ? window.EdenI18n.t("text_20e072962e84") : window.EdenI18n.t("text_0f40151cebe5"));
    }

    setStatus(picker) {
      if (!picker.edenStatus) return;
      picker.edenStatus.textContent = this.loading ? window.EdenI18n.t("text_4f803cbcdef7") : this.error ? window.EdenI18n.t("text_7ca18a6e8d32") : window.EdenI18n.t("text_3d3a4fd02245");
      picker.edenStatus.classList.toggle('is-error', this.error && !this.loading);
      picker.edenRetry.hidden = !this.error || Boolean(this.loading);
      picker.calendarContainer.setAttribute('aria-busy', this.loading ? 'true' : 'false');
    }

    async load(start, count) {
      this.loading++;
      this.pickers.forEach(fp => this.setStatus(fp));
      try {
        const request = {hotelId:this.hotelId,start,days:count};
        if (this.roomId) request.roomId = this.roomId;
        const data = await fetchCalendar(request);
        Object.assign(this.days, data.days);
        this.loadedAt = Date.parse(data.checkedAt) || Date.now();
        this.error = false;
      } catch {
        this.error = true;
      } finally {
        this.loading--;
        this.pickers.forEach(fp => { fp.redraw(); this.setStatus(fp); });
      }
    }

    queueVisible(picker, refresh = false) {
      clearTimeout(picker.edenLoadTimer);
      picker.edenLoadTimer = setTimeout(() => this.loadVisible(picker, refresh), 120);
    }

    async loadVisible(picker, refresh = false) {
      const first = new Date(picker.currentYear, picker.currentMonth, 1, 12);
      const last = new Date(picker.currentYear, picker.currentMonth + picker.config.showMonths, 0, 12);
      const start = iso(first) < this.today ? this.today : iso(first);
      const end = iso(last);
      if (end < start) return;
      const missing = [];
      for (let date = start; date <= end; date = addDays(date, 1)) if (refresh || !this.days[date]) missing.push(date);
      if (missing.length) await this.load(missing[0], Math.min(62, distance(missing[0], missing[missing.length - 1]) + 1));
    }
  }

  window.EdenStayCalendar = {
    mount(options) {
      if (!window.flatpickr || !options.root || !options.checkin || !options.checkout || !options.hotelId) return null;
      if (options.root.classList.contains('eg-rp--collection-proxy')) return null;
      if (options.root.edenStayCalendar) return options.root.edenStayCalendar;
      const instance = new EdenStayCalendar(options);
      options.root.edenStayCalendar = instance;
      return instance;
    },
  };
})();
